import React, { useEffect, useState } from 'react'
import {
  Form,
  FormGrid,
  Input,
  InputGrid,
  NextButton,
  OptInLink,
  OptInMessage,
  OrganizationName,
  ProgressGrid,
  Title,
} from '@/components/registrant/Registrant.styled'
import { Controller, useForm } from 'react-hook-form'
import SearchOrganization from '@/components/organization/SearchOrganization'
import AppInputMask from '@/components/ui/InputMask'
import { Box, InputAdornment, Typography, useMediaQuery, useTheme } from '@mui/material'
import { PrivacyPolicy } from '@/components/legalInformation'
import { AppButton, Tooltip, TooltipArrow, TooltipContentWrapper } from '@/components/ui'
import { HelpBlock } from '@/components/help/HelpBlock'
import { Organization } from '@/interfaces/organization'
import { Utils } from '@/lib/utils'
import { useOrganization, useTooltip } from '@/hooks'
import Link from 'next/link'
import RegistrantNotice from '@/components/registrant/RegistrantNotice'
import Grid from '@mui/material/Grid2'

export interface FormValues {
  organizationMidasId: number | string
  phone: string
}

interface Props {
  onSubmit: (data: FormValues) => void
  inProgress?: boolean
}

const RegistrationForm: React.FC<Props> = ({ onSubmit, inProgress }) => {
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    control,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      phone: '',
      organizationMidasId: '',
    },
  })
  const { loadOrganizationByMidasId } = useOrganization()
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(undefined)
  const watchOrganizationMidasId = watch('organizationMidasId')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    setTriggerRef,
    setTooltipRef,
    getTooltipProps,
    getArrowProps,
    visible: isTooltipVisible,
  } = useTooltip({ trigger: 'hover', placement: 'left' })

  useEffect(() => {
    if (watchOrganizationMidasId) {
      return
    }

    setSelectedOrganization(undefined)
  }, [watchOrganizationMidasId])

  useEffect(() => {
    ;(async () => {
      await handleOrgIdFromUrl()
    })()
  }, [])

  const selectOrganization = (org: Organization) => {
    setValue('organizationMidasId', org.midasId, { shouldValidate: true })
    setSelectedOrganization(org)
  }

  const handleOrgIdFromUrl = async () => {
    const orgId = Utils.getQueryParamByName(location.search, 'org_id')
    if (!Utils.isNumber(orgId)) return

    const organization = await loadOrganizationByMidasId(parseInt(orgId, 10))
    if (!organization) return

    selectOrganization(organization)
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGrid container wrap="nowrap" direction="column" justifyContent="space-between">
        <Title variant="h2">Register Participant</Title>
        <InputGrid>
          <Controller
            name="organizationMidasId"
            control={control}
            rules={{
              required: 'Organization ID is required',
            }}
            render={({ field }) => (
              <Input
                {...field}
                fullWidth
                label="Your organization ID"
                variant="outlined"
                error={Boolean(errors.organizationMidasId && errors.organizationMidasId.message)}
                helperText={errors.organizationMidasId && errors.organizationMidasId.message}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedOrganization ? (
                          <OrganizationName variant="body2">{selectedOrganization.name}</OrganizationName>
                        ) : null}
                        <SearchOrganization onSelectOrganization={selectOrganization} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'Mobile number is required',
            }}
            render={({ field }) => (
              <Input
                {...field}
                fullWidth
                label="Your mobile number"
                variant="outlined"
                error={Boolean(errors.phone && errors.phone.message)}
                helperText={errors.phone && errors.phone.message}
                placeholder="+1 XXX-XXX-XXXX"
                slotProps={{
                  input: {
                    inputComponent: AppInputMask as any,
                  },
                }}
              />
            )}
          />
          <OptInMessage variant="body2">
            By entering your number you are consenting to be contacted via SMS & email by Charleston Wrap on behalf of
            your organization. You may receive only 3 SMS messages related to this fundraiser.
            <br />
            Reply STOP to opt out. View our <OptInLink as={PrivacyPolicy} />
          </OptInMessage>
          <NextButton ref={setTriggerRef} fullWidth variant="contained" type="submit" disabled={inProgress}>
            Next
          </NextButton>
          {isMobile ? (
            <RegistrantNotice />
          ) : (
            isTooltipVisible && (
              <Tooltip ref={setTooltipRef} {...getTooltipProps({ className: 'tooltip-container' })}>
                <TooltipArrow {...getArrowProps({ className: 'tooltip-arrow' })} />
                <TooltipContentWrapper>
                  <RegistrantNotice variant="tooltip" />
                </TooltipContentWrapper>
              </Tooltip>
            )
          )}
        </InputGrid>
        <ProgressGrid>
          <Box
            sx={{
              mt: 4,
              mb: 2,
            }}
          >
            <Typography>Already have an account?</Typography>
          </Box>
          <AppButton fullWidth LinkComponent={Link} href="/registrant/sign-in" variant="outlined">
            Check progress
          </AppButton>
        </ProgressGrid>
        <Grid>
          <HelpBlock />
        </Grid>
      </FormGrid>
    </Form>
  )
}

export default RegistrationForm
